import { useTranslation } from 'react-i18next';
import { CloseButton } from '@components/close-button';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { ModalV2 } from '@components/modals/modal-v2';

import { AddOnsPresetsModalContentProps } from '../add-ons-presets-modal.type';

import { AddOnPresetRow } from './add-on-preset-row';
import { PresetsSkeleton } from './presets-skeleton';

export const ADD_ONS_PRESETS_MODAL_TEST_ID = 'add-ons-presets-modal';

export const AddOnsPresetsModalContent = ({
  isOpen,
  onClose,
  presets,
  isLoading = false,
  selectedIds,
  onToggle,
  onSave,
  isSaving = false,
  surfaceClassName,
  zIndex,
}: AddOnsPresetsModalContentProps) => {
  const { t } = useTranslation();

  const isEmpty = !isLoading && presets.length === 0;
  const hasNoSelection = selectedIds.length === 0;

  const renderBody = () => {
    if (isLoading) return <PresetsSkeleton dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--skeleton`} />;

    if (isEmpty) {
      return (
        <div
          className='flex max-sm:min-h-50 items-center justify-center'
          data-testid={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--empty`}>
          <p className='text-secondary text-body-m text-center'>{t('errors:assignAddonsEmptyError')}</p>
        </div>
      );
    }

    return presets.map((addon) => (
      <AddOnPresetRow
        key={addon.id}
        addon={addon}
        checked={selectedIds.includes(addon.id)}
        onToggle={onToggle}
        disabled={isSaving}
        dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--row-${addon.id}`}
      />
    ));
  };

  return (
    <ModalV2
      isOpen={isOpen}
      onClose={onClose}
      maxWidthClass='sm:max-w-2xl'
      hasMobileHandle
      enableDragToClose
      ariaLabel='add-ons-presets'
      surfaceClassName={surfaceClassName}
      zIndex={zIndex}>
      <ModalStickyHeader
        title={t('offers:admissionAddOnsSectionTitle')}
        customRightElement={
          <div className='hidden sm:block'>
            <CloseButton
              handleClose={onClose}
              id={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--close`}
              dataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--close`}
            />
          </div>
        }
      />

      <div className='flex flex-col gap-2 px-6 py-4'>{renderBody()}</div>

      <ModalStickyFooter
        onSecondaryButtonClick={onClose}
        secondaryButtonText={t('common:cancel')}
        secondaryDataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--cancel`}
        onPrimaryButtonClick={onSave}
        primaryButtonText={t('common:save')}
        primaryDataTestId={`${ADD_ONS_PRESETS_MODAL_TEST_ID}--save`}
        isSubmitDisabled={isEmpty || hasNoSelection}
        isSubmitLoading={isSaving}
      />
    </ModalV2>
  );
};
